import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { useProjectIssues } from '../../IssuesPage/hooks/useProjectIssues';
import { itemsInView, relatedTasksIndex } from '@d-orca/shared/client/activities';
import { activityToListItem, issueToListItem, type IListItem } from '../model/listItem';
import { sortListItems } from '../model/sort';
import { projectToday, type IActivityFilters } from '../activitiesFilter';
import type { IPlanWindow } from '../WorkPlan/workPlanModel';
import { useActivitiesFromSchedule } from './useActivitiesFromSchedule';
import { useSiteRestDays } from '@hooks/useSiteRestDays';

/**
 * `window` is the page's date window (its date picker), when it has one: the
 * list keeps what overlaps it — an activity's span (an overdue one runs to
 * today), an issue's due (or created) day — so the list and the Work Plan
 * hold the same activities. `filters.hiddenStatuses` are the page's Status checkboxes: the
 * statuses the list hides — the Work Plan's: an activity's rung, an issue's
 * keyed status.
 */
export function useUnifiedList(
  siteId: string | undefined,
  filters: IActivityFilters & { hiddenStatuses?: readonly string[] },
  window: Pick<IPlanWindow, 'from' | 'to'> | null = null,
) {
  const sched = useActivitiesFromSchedule(siteId);
  const issuesQ = useProjectIssues(siteId ?? null);
  const restDays = useSiteRestDays(siteId, sched.weeklyDaysOff);
  /* "Today" is the SITE's today — a site ahead of the viewer has already rolled
     over, and every date filter and risk derivation below keys off it. While the
     working site is unresolved the hook reports loading rather than dating the
     whole list against a guessed zone. */
  const tz = useSelector(selectSiteTz);
  const today = tz ? projectToday(tz) : '';

  // Build the full unified list — the expensive pass (per-item risk/date derivation
  // across every task & issue). It does NOT depend on the filter controls, so it
  // rebuilds only when the underlying data changes, not when the user flips a filter.
  // At ~3-4k tasks this is the work that froze the UI on every filter switch when it
  // was inlined with the filter step. Activities and issues build apart, so an
  // issues refetch leaves the activities — and the tracker built over them — be.
  const planTasks = sched.detail?.tasks;
  const activityItems = useMemo<IListItem[]>(() => {
    if (!tz) return [];
    // Neighbours resolve against the WHOLE plan: a committed activity's
    // predecessor is still its predecessor before it is committed.
    const relatedOf = relatedTasksIndex(sched.tasks, sched.deps, planTasks);
    return sched.tasks.map((t) => {
      const activityStatus = sched.activityStatusById.get(t.id);
      return activityToListItem(t, {
        today,
        // The server's flag, solved over the WHOLE plan: this feed holds only
        // the committed tasks, and a path solved over them is meaningless.
        isCritical: t.critical === true,
        relatedTasks: relatedOf(t.id),
        ...(activityStatus ? { activityStatus } : {}),
      });
    });
  }, [sched.tasks, sched.deps, sched.activityStatusById, planTasks, today, tz]);
  const allItems = useMemo<IListItem[]>(() => {
    if (!tz) return [];
    return [...activityItems, ...(issuesQ.data?.issues ?? []).map((i) => issueToListItem(i, { today, tz }))];
  }, [activityItems, issuesQ.data, today, tz]);

  // Cheap per-change pass: the page's one "in view" selector over the prebuilt
  // list, then the sort. This is ALL that runs when the user changes a filter,
  // so a filter switch stays well under a frame.
  const items = useMemo<IListItem[]>(
    () => sortListItems(itemsInView(allItems, filters, { today, restDays, window })),
    [allItems, filters, today, restDays, window],
  );

  return {
    items,
    // The activities alone, unfiltered, and what the page's views filter them
    // by — the Progress Tracker runs the same selector over them.
    activityItems,
    today,
    restDays,
    // The unfiltered list — the drawer resolves `?openIssue=` against this so a
    // deep-link opens even when the item is filtered out of the visible Daily
    // List. The Work Plan filters it itself.
    allItems,
    activitiesLoading: sched.isLoading || !tz,
    issuesLoading: issuesQ.isLoading,
    // Only an error with NO data to show should surface DataUnavailable. A failed
    // background refetch keeps its last data, so it must not blow away the list.
    activitiesError: sched.isError && !sched.hasData,
    issuesError: issuesQ.isError && issuesQ.data == null,
  };
}
